import { isAllDayEvent } from './eventDates';
import {
  eventDateKey,
  eventDayStart,
  instantToEventDateTime,
  parseDateLabel,
  resolveEventDateTimeInput,
  type EventTemporalErrorReason,
  type EventTemporalInput,
  type EventTemporalResult,
} from './eventTemporal';

/** Timed inputs are NY wall clocks; all-day inputs are source date labels. */
export function isoToLocalInput(iso?: string | null, isAllDay = false): string {
  if (!iso) return '';
  if (isAllDay) return eventDateKey({ startUtc: iso, isAllDay: true }) ?? '';
  const result = instantToEventDateTime(iso);
  return result.ok ? result.value.localInput : '';
}

/**
 * Resolve NY input explicitly. Only the exact original wall-clock value can
 * reuse its known fall-fold offset; a newly entered repeated hour is ambiguous.
 * Preserve the original spelling/instant when unchanged, including precision.
 */
export function localInputToIso(local: string, originalIso?: string | null): EventTemporalResult<string> {
  const original = originalIso ? instantToEventDateTime(originalIso) : null;
  if (original?.ok) {
    // Browsers may normalize trailing :00 or fractional zeroes in input values.
    // Reusing an offset is safe only if the resolved instant is still identical.
    const sameInstant = resolveEventDateTimeInput(local, original.value.offset);
    if (sameInstant.ok && sameInstant.value.isoUtc === original.value.isoUtc) {
      return { ok: true, value: originalIso! };
    }
  }
  const result = resolveEventDateTimeInput(local);
  if (!result.ok) return result;
  return { ok: true, value: result.value.isoUtc };
}

export interface EventDateEditInput {
  startLocal: string;
  endLocal: string;
  isAllDay: boolean;
}

export interface EventDateEditValue {
  startUtc: string;
  /** null deliberately clears an existing end; undefined leaves an absent end absent. */
  endUtc?: string | null;
  isAllDay: boolean;
}

export type EventDateEditResult =
  | { ok: true; value: EventDateEditValue }
  | { ok: false; field: 'start' | 'end'; reason: EventTemporalErrorReason };

/** Change input mode without converting a calendar label through an instant. */
export function eventInputForAllDay(input: string, isAllDay: boolean): string {
  if (!input) return '';
  return isAllDay ? input.split('T')[0] : input.includes('T') ? input : `${input}T00:00`;
}

/** Shared validation for both the edit modal and inline date fixer. */
export function resolveEventDateEdit(input: EventDateEditInput, original?: EventTemporalInput): EventDateEditResult {
  const originalAllDay = original ? isAllDayEvent(original) : false;
  const resolve = (local: string, originalIso?: string | null): EventTemporalResult<string> => {
    if (!input.isAllDay) return localInputToIso(local, originalAllDay ? undefined : originalIso);
    if (!parseDateLabel(local)) return { ok: false, reason: 'invalid-date' };
    if (originalAllDay && originalIso && eventDateKey({ startUtc: originalIso, isAllDay: true }) === local) {
      return { ok: true, value: originalIso };
    }
    const result = eventDayStart(local);
    return result.ok ? { ok: true, value: result.value.isoUtc } : result;
  };
  const start = resolve(input.startLocal, original?.startUtc);
  if (!start.ok) return { ok: false, field: 'start', reason: start.reason };
  const end = input.endLocal ? resolve(input.endLocal, original?.endUtc) : null;
  if (end && !end.ok) return { ok: false, field: 'end', reason: end.reason };
  if (end?.ok) {
    const reversed = input.isAllDay
      ? input.endLocal < input.startLocal
      : Date.parse(end.value) < Date.parse(start.value);
    if (reversed) return { ok: false, field: 'end', reason: 'invalid-range' };
  }
  return {
    ok: true,
    value: {
      startUtc: start.value,
      endUtc: end?.ok ? end.value : original?.endUtc ? null : undefined,
      isAllDay: input.isAllDay,
    },
  };
}

export function eventDateEditError(result: Extract<EventDateEditResult, { ok: false }>): string {
  const field = result.field === 'start' ? 'Start' : 'End';
  switch (result.reason) {
    case 'nonexistent-local-time':
      return `${field} time does not exist because the clocks spring forward. Choose a time outside the skipped hour.`;
    case 'ambiguous-local-time':
      return `${field} time occurs twice when the clocks fall back. Keep the original time unchanged or choose a time outside the repeated hour.`;
    case 'invalid-range':
      return 'End must be on or after the start date/time.';
    default:
      return `${field} date/time is required and must be valid.`;
  }
}
